const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface PurchaseRequestItem {
  id?: number;
  service_id?: string;
  pack_id?: string;
  service?: number;
  pack?: number;
  service_name?: string;
  pack_name?: string;
  type: 'service' | 'pack';
  quantity: number;
  pages?: number;
  description?: string;
  unit_price: number;
  total_price: number;
  created_at?: string;
}

export interface InvoiceHistoryEntry {
  id: number;
  previous_status?: string;
  previous_status_display?: string;
  new_status: string;
  new_status_display: string;
  action: string;
  action_display: string;
  comment?: string;
  changed_by?: number;
  changed_by_name?: string;
  snapshot_data?: any;
  created_at: string;
}

export interface TimelineEntry {
  id: number;
  date: string;
  status: string;
  status_display: string;
  action: string;
  action_display: string;
  comment?: string;
  changed_by?: string;
  snapshot?: any;
}

export interface PurchaseRequest {
  id?: number;
  invoice_id?: string;
  user_id: string;
  user_name?: string;
  status: 'confirmed' | 'revision' | 'pending' | 'cancelled';
  status_display?: string;
  subtotal: number;
  discount_percentage?: number;
  discount_amount?: number;
  tva_percentage?: number;
  tva_amount?: number;
  total_amount?: number;
  promo_code?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_company?: string;
  client_address?: string;
  has_variable_pricing: boolean;
  items: PurchaseRequestItem[];
  history?: InvoiceHistoryEntry[];
  timeline?: TimelineEntry[];
  created_at?: string;
  updated_at?: string;
}

export interface CreatePurchaseRequestData {
  status: 'confirmed' | 'revision' | 'pending';
  subtotal: number;
  discount_percentage: number;
  discount_amount: number;
  tva_percentage: number;
  tva_amount: number;
  total_amount: number;
  promo_code?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_company?: string;
  client_address?: string;
  has_variable_pricing: boolean;
  items: PurchaseRequestItem[];
}

class PurchaseRequestApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    console.log('🔑 Token utilisé pour auth:', token ? `${token.substring(0, 20)}...` : 'AUCUN TOKEN');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async createPurchaseRequest(data: CreatePurchaseRequestData): Promise<PurchaseRequest> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur détaillée du serveur:', errorData);
        throw new Error(errorData.message || errorData.detail || JSON.stringify(errorData) || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la création de la demande d\'achat:', error);
      throw error;
    }
  }

  async getPurchaseRequests(): Promise<PurchaseRequest[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes d\'achat:', error);
      throw error;
    }
  }

  async getAllPurchaseRequests(): Promise<PurchaseRequest[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/all/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération de toutes les demandes d\'achat:', error);
      throw error;
    }
  }

  async getPurchaseRequest(id: string): Promise<PurchaseRequest> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/${id}/detail/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération de la demande d\'achat:', error);
      throw error;
    }
  };

  async getPurchaseRequestAdmin(id: string): Promise<PurchaseRequest> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/${id}/detail/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération de la demande d\'achat (admin):', error);
      throw error;
    }
  }

  async getUserPurchaseRequests(): Promise<PurchaseRequest[]> {
    try {
      console.log('🔍 Récupération des factures utilisateur...');
      
      // Utiliser l'endpoint spécifique pour l'utilisateur avec historique
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/user/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      console.log('📡 Réponse API user:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('📋 Factures utilisateur récupérées:', data);
      
      // Debug détaillé de chaque facture
      if (Array.isArray(data)) {
        data.forEach((invoice, index) => {
          console.log(`📄 Facture ${index + 1} (ID: ${invoice.id}):`, {
            status: invoice.status,
            created_at: invoice.created_at,
            updated_at: invoice.updated_at,
            total_amount: invoice.total_amount,
            items_count: invoice.items?.length || 0
          });
        });
      }
      
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des factures utilisateur:', error);
      throw error;
    }
  }

  async updatePurchaseRequest(id: string, data: CreatePurchaseRequestData): Promise<PurchaseRequest> {
    try {
      console.log(`🔄 Mise à jour de la facture ${id} avec:`, data);
      
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/${id}/`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      console.log(`📡 Réponse updatePurchaseRequest (${id}):`, response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Erreur updatePurchaseRequest (${id}):`, errorText);
        throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Facture ${id} mise à jour avec historique:`, result);
      
      // Vérifier que le backend a bien mis à jour les champs importants
      console.log(`🔍 Vérification de la mise à jour:`, {
        id: result.id,
        status: result.status,
        updated_at: result.updated_at,
        total_amount: result.total_amount,
        items_count: result.items?.length || 0,
        history_count: result.history?.length || 0,
        latest_status: result.timeline?.[result.timeline?.length - 1]?.status || 'N/A'
      });
      
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la demande d\'achat:', error);
      throw error;
    }
  }

  async updatePurchaseRequestStatus(id: string, status: 'confirmed' | 'revision' | 'pending' | 'cancelled'): Promise<PurchaseRequest> {
    try {
      console.log(`🔄 Mise à jour du statut de la facture ${id} vers:`, status);
      
      const response = await fetch(`${API_BASE_URL}/api/purchase-requests/${id}/status/`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      console.log(`📡 Réponse updatePurchaseRequestStatus (${id}):`, response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Erreur updatePurchaseRequestStatus (${id}):`, errorText);
        throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Statut de la facture ${id} mis à jour:`, result);
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }
}

export const purchaseRequestApiService = new PurchaseRequestApiService();
